<!-- 组件设计：CommonSearch查询处理handleQuery放在父组件页面；
  CommonPagination数据pageData:{pageNum,pageSize,totalCount}在页面内使用hooks统一生成，可供CommonSearch、CommonPagination修改使用；
  CommonTable只做展示，不操作其他组件数据 -->
<template>
  <div class="common-search-component">
    <el-form :model="formData" :inline="true">
      <el-row>
        <el-col
          :span="item.elColSpan || props.elColSpan || 6"
          class="search-form-item"
          v-for="item in propsList"
          :key="item.prop"
        >
          <el-form-item
            class="item-style"
            :label-width="props.labelWidth"
            :label="props.showLable ? item.label : ''"
            :prop="item.prop"
          >
            <slot v-if="item.slotName" :name="item.slotName" :prop="item"></slot>
            <template v-else>
              <!-- 嵌套的ref不会解包 -->

              <!-- 10 输入框 -->
              <el-input
                v-if="item.type == '10'"
                v-model="formData[item.prop].value"
                :placeholder="'请输入' + item.label"
                :clearable="'clearable' in item ? !!item.clearable : true"
                :disabled="'disabled' in item ? !!item.disabled : false"
              ></el-input>

              <!-- 20 日期区间选择框 -->
              <el-date-picker
                v-if="item.type == '20'"
                type="daterange"
                v-model="formData[item.prop].value"
                value-format="YYYY-MM-DD"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                :clearable="'clearable' in item ? !!item.clearable : true"
                :disabled="'disabled' in item ? !!item.disabled : false"
              ></el-date-picker>
              <!-- 21 日期时间区间选择框， HH:mm:ss 24时制，默认时刻从00:00:00开始，hh:mm:ss 12时制，默认时刻从12:00:00开始，-->
              <el-date-picker
                v-if="item.type == '21'"
                type="datetimerange"
                v-model="formData[item.prop].value"
                value-format="YYYY-MM-DD HH:mm:ss"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                :shortcuts="item.shortcuts"
                :disabled-date="item.disabledDate"
                :clearable="'clearable' in item ? !!item.clearable : true"
                :disabled="'disabled' in item ? !!item.disabled : false"
              ></el-date-picker>

              <!-- 30 下拉选择框 -->
              <el-select
                v-if="item.type == '30'"
                v-model="formData[item.prop].value"
                :placeholder="'请选择' + item.label"
                :clearable="'clearable' in item ? !!item.clearable : true"
                :disabled="'disabled' in item ? !!item.disabled : false"
              >
                <!--  el-select 选项值集，默认数据结构[{label:'',value:''}] -->
                <el-option
                  v-for="(optionItem, optionItemIndex) in item.options"
                  :key="optionItemIndex"
                  :label="optionItem[item?.optionItemKeysMap?.label || 'label']"
                  :value="optionItem[item?.optionItemKeysMap?.value || 'value']"
                ></el-option>
              </el-select>

              <!-- 50 自动补全输入框 -->
              <el-autocomplete
                v-if="item.type == '50'"
                v-model="formData[item.prop].value"
                :fetch-suggestions="item.queryFetch"
                :placeholder="'请选择' + item.label"
                :clearable="'clearable' in item ? !!item.clearable : true"
                :disabled="'disabled' in item ? !!item.disabled : false"
              ></el-autocomplete>
            </template>
          </el-form-item>
        </el-col>
        <el-col :span="propsList.length <= 2 ? 8 : 4" class="search-form-item">
          <el-form-item>
            <el-button size="default" @click="handleEmit('resetAndQuery')">重置</el-button>
            <el-button type="primary" size="default" @click="handleEmit('query')">查询</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref, toRefs, onBeforeMount, watch } from 'vue'
import { BaseObject } from '@/types/index'
// import { findValueBySetCode } from '@/api/common'

// el-select options 选项类型
interface OptionItem {
  label?: string
  value?: any
  [index: string]: any
}
// el-option label/value 使用其他字段名的映射
interface OptionItemKeysMap {
  label: string
  value: string
}

// el-form-item 数据类型
interface SearchItem {
  type: '10' | '20' | '21' | '30' | '40' | '50' | '60' // 10:el-input, 20:el-date-picker, 30:el-select,  50:el-autocomplete,
  label: string // 表单项 label
  prop: string // 表单项 prop
  clearable?: boolean // 表单项 是否可清除，默认 true
  options?: OptionItem[] // 表单项是el-select且选项是值集时，查询到的选项列表，接口数据结构 [{value:'', name:''}]
  valueSetCode?: string // 表单项是el-select且选项是值集时，查询值集的code
  optionItemKeysMap?: OptionItemKeysMap // 表单项是el-select且选项非值集时，<el-option>使用的属性的映射
  slotName?: string // 插槽名，有值即表示使用插槽
  disabled?: boolean // 是否禁用
  elColSpan?: number // el-form-item :span的值
  showAllLevels?: boolean // 表单项是el-cascader时，show-all-levels
  queryFetch?: (queryString: string, cb: any) => void // 表单项是el-autocomplete时，fetch-suggestions
  shortcuts?: any[] //20 21 时间选择框 快捷选项
  disabledDate?: any //20 21 时间选择框 禁止选择的日期
}
// 如何定义PropsValue的对象类型，使包含所有propsList的prop，运行时确定propsList，无法校验？？？TODO
// interface PropsValue<T extends SearchItem> {
//   [p in keyof T]:any
// }
// props的类型声明、默认值。（vue3要求，类型声明参数：类型字面量；在同一文件中的接口或类型字面量的引用）
const props = withDefaults(
  defineProps<{
    propsList: SearchItem[]
    propsValue: BaseObject
    labelWidth?: string
    elColSpan?: number
    showLable?: boolean
  }>(),
  {
    propsList: () => [],
    propsValue: () => ({}),
    labelWidth: 'auto',
    elColSpan: 6,
    showLable: true,
  },
)
let formData = toRefs(props.propsValue) //defineProps声明的属性，不可修改，vue已设置为readonly，但是props.key.key可修改。propsValue是在父组件声明的reactive数据，传入子组件保持响应式

const emit = defineEmits(['resetAndQuery', 'query'])
const handleEmit = (type: 'resetAndQuery' | 'query') => {
  //此处 type 不能使用 type:string，emit 第一个参数被vue定义为defineEmits的联合类型
  emit(type)
}

onBeforeMount(() => {
  // 数据字典：el-select 选项值集，默认数据结构[{name:'',value:''}]。后端暂无相应接口 TODO
  // props.propsList.map(item => {
  //   if (item.valueSetCode) {
  //     findValueBySetCode({ valueSetCode: item.valueSetCode })
  //       .then((response: any) => {
  //         if (response.statusCode == 200) {
  //           item.options = response.responseData
  //         }
  //       })
  //       .catch(error => {
  //         console.log('CommonSearch-findValueBySetCode-error', error)
  //       })
  //   }
  // })
})
onMounted(() => {})
</script>

<style scoped lang="less">
@import '@/assets/css/searchAndTableBase.less';
.common-search-component {
}
</style>
